'use client'

import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import type { ElementRef, ReactElement, ReactNode, Ref } from 'react'
import { useState } from 'react'
import type {
  ActionMeta,
  GroupBase,
  MultiValue,
  OptionsOrGroups,
  SingleValue
} from 'react-select'

import type { AsyncMultipleSearchWithSelectProps } from '~/core/ui/AsyncMultipleSearchWithSelect'
import { AsyncMultipleSearchWithSelect } from '~/core/ui/AsyncMultipleSearchWithSelect'
import type { AsyncSingleSearchWithSelectProps } from '~/core/ui/AsyncSingleSearchWithSelect'
import { AsyncSingleSearchWithSelect } from '~/core/ui/AsyncSingleSearchWithSelect'
import type { ISelectOption } from '~/core/ui/Select'

// need to fix
// eslint-disable-next-line import/no-restricted-paths
import AsyncMultipleSearchWithSelectOptionGroup from '~/components/Settings/Members/AsyncMultipleSearchWithSelectOptionGroup'

interface DropdownOptionAsyncProps {
  defaultOpen?: boolean
  trigger?: ReactNode
  renderTrigger?: (args: {
    onOpenChange: (open: boolean) => void
    open: boolean
  }) => ReactNode
  side?: 'top' | 'right' | 'bottom' | 'left'
  align?: 'start' | 'center' | 'end'
  sideOffset?: number
  className?: string
  configSelect: AsyncMultipleSearchWithSelectProps &
    AsyncSingleSearchWithSelectProps & {
      isMulti?: boolean
      isMultiGroup?: boolean
      customerDropdownHeader?: ReactElement<any>
      mappingGroupData?: (
        data?: ISelectOption[]
      ) => OptionsOrGroups<ISelectOption, GroupBase<ISelectOption>>
      forceOnBlurWhenCloseDropdown?: (e?: any, selectRef?: any) => void
    }
  portalContainer?: HTMLElement | null
  dropdownMenuContainer?: HTMLElement | null
  isDisabled?: boolean
  isCloseDropdownOnSelect?: boolean
  limit?: number
  loadAsyncWhenOpen?: boolean
  ref?: Ref<ElementRef<typeof DropdownMenu.Content>>
}

const DropdownOptionAsync = (props: DropdownOptionAsyncProps) => {
  const {
    defaultOpen = false,
    trigger,
    renderTrigger,
    side = 'top',
    align = 'start',
    sideOffset = 5,
    className,
    portalContainer,
    dropdownMenuContainer,
    isDisabled,
    configSelect: { value, onChange, destructive, ...otherConfigSelect },
    limit = 25,
    loadAsyncWhenOpen = true,
    isCloseDropdownOnSelect = false,
    ref
  } = props
  const [open, onOpenChange] = useState(defaultOpen)
  const mergedOptions = {
    isDropdown: true,
    autoFocus: true,
    showDropdownIndicator: false,
    showClearIndicator: false,
    backspaceRemovesValue: false,
    controlShouldRenderValue: false,
    hideSelectedOptions: false,
    isClearable: false,
    menuIsOpen: true,
    tabSelectsValue: false,
    value,
    limit,
    loadAsyncWhenOpen,
    onChange: (
      newValue: SingleValue<ISelectOption> | MultiValue<ISelectOption> | null,
      actionMeta: ActionMeta<ISelectOption>
    ) => {
      if (actionMeta?.option?.disabled) {
        return
      }
      if (isCloseDropdownOnSelect) {
        onOpenChange(false)
      }
      onChange(newValue, actionMeta)
    },
    ...otherConfigSelect
  }

  return (
    <DropdownMenu.Root
      open={open}
      onOpenChange={(open) => {
        if (!open && mergedOptions?.forceOnBlurWhenCloseDropdown) {
          mergedOptions.forceOnBlurWhenCloseDropdown()
        }
        onOpenChange(open)
      }}>
      {trigger ? (
        <DropdownMenu.Trigger disabled={isDisabled} asChild>
          <div>{trigger}</div>
        </DropdownMenu.Trigger>
      ) : null}
      {renderTrigger && <>{renderTrigger({ onOpenChange, open })}</>}

      <DropdownMenu.Portal container={portalContainer}>
        <DropdownMenu.Content
          ref={ref}
          align={align}
          side={side}
          sideOffset={sideOffset}
          onEscapeKeyDown={(e) => onOpenChange(false)}
          className={`shadow-ats dark:shadow-dark-ats my-ignore-action rounded-xs bg-white dark:bg-gray-900 ${className}`}>
          {otherConfigSelect.customerDropdownHeader &&
            otherConfigSelect.customerDropdownHeader}
          {otherConfigSelect?.isMultiGroup ? (
            <AsyncMultipleSearchWithSelectOptionGroup
              showSelectOptionAll={false}
              {...mergedOptions}
            />
          ) : otherConfigSelect?.isMulti ? (
            <AsyncMultipleSearchWithSelect {...mergedOptions} />
          ) : (
            <AsyncSingleSearchWithSelect {...mergedOptions} />
          )}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  )
}

DropdownOptionAsync.displayName = 'DropdownOptionAsync'

export { DropdownOptionAsync }
export type { DropdownOptionAsyncProps }
